/**
 * A 5x7 bitmap font for alpha bracelets.
 *
 * Orientation matters here. On a real alpha name bracelet the text reads along
 * the LENGTH of the bracelet, so a letter's height maps across the bracelet's
 * width (the base strings) and its width maps along the direction you knot.
 * `renderWord` does that rotation; the glyphs below are stored the normal way
 * up so they stay editable by eye.
 *
 * 5x7 is the smallest size that keeps letters legible once they are knotted at
 * roughly 1.5 mm per string: 7 tall plus a 2-string margin each side gives an
 * 11-string bracelet, which is a comfortable, common width.
 */

const GLYPHS: Record<string, string[]> = {
  A: ['.###.', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
  B: ['####.', '#...#', '#...#', '####.', '#...#', '#...#', '####.'],
  C: ['.###.', '#...#', '#....', '#....', '#....', '#...#', '.###.'],
  D: ['####.', '#...#', '#...#', '#...#', '#...#', '#...#', '####.'],
  E: ['#####', '#....', '#....', '####.', '#....', '#....', '#####'],
  F: ['#####', '#....', '#....', '####.', '#....', '#....', '#....'],
  G: ['.###.', '#...#', '#....', '#.###', '#...#', '#...#', '.###.'],
  H: ['#...#', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
  I: ['#####', '..#..', '..#..', '..#..', '..#..', '..#..', '#####'],
  J: ['..###', '...#.', '...#.', '...#.', '...#.', '#..#.', '.##..'],
  K: ['#...#', '#..#.', '#.#..', '##...', '#.#..', '#..#.', '#...#'],
  L: ['#....', '#....', '#....', '#....', '#....', '#....', '#####'],
  M: ['#...#', '##.##', '#.#.#', '#.#.#', '#...#', '#...#', '#...#'],
  N: ['#...#', '##..#', '#.#.#', '#.#.#', '#..##', '#...#', '#...#'],
  O: ['.###.', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  P: ['####.', '#...#', '#...#', '####.', '#....', '#....', '#....'],
  Q: ['.###.', '#...#', '#...#', '#...#', '#.#.#', '#..#.', '.##.#'],
  R: ['####.', '#...#', '#...#', '####.', '#.#..', '#..#.', '#...#'],
  S: ['.####', '#....', '#....', '.###.', '....#', '....#', '####.'],
  T: ['#####', '..#..', '..#..', '..#..', '..#..', '..#..', '..#..'],
  U: ['#...#', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  V: ['#...#', '#...#', '#...#', '#...#', '#...#', '.#.#.', '..#..'],
  W: ['#...#', '#...#', '#...#', '#.#.#', '#.#.#', '##.##', '#...#'],
  X: ['#...#', '#...#', '.#.#.', '..#..', '.#.#.', '#...#', '#...#'],
  Y: ['#...#', '#...#', '.#.#.', '..#..', '..#..', '..#..', '..#..'],
  Z: ['#####', '....#', '...#.', '..#..', '.#...', '#....', '#####'],
  '0': ['.###.', '#...#', '#..##', '#.#.#', '##..#', '#...#', '.###.'],
  '1': ['..#..', '.##..', '..#..', '..#..', '..#..', '..#..', '.###.'],
  '2': ['.###.', '#...#', '....#', '...#.', '..#..', '.#...', '#####'],
  '3': ['#####', '...#.', '..#..', '...#.', '....#', '#...#', '.###.'],
  '4': ['...#.', '..##.', '.#.#.', '#..#.', '#####', '...#.', '...#.'],
  '5': ['#####', '#....', '####.', '....#', '....#', '#...#', '.###.'],
  '6': ['..##.', '.#...', '#....', '####.', '#...#', '#...#', '.###.'],
  '7': ['#####', '....#', '...#.', '..#..', '.#...', '.#...', '.#...'],
  '8': ['.###.', '#...#', '#...#', '.###.', '#...#', '#...#', '.###.'],
  '9': ['.###.', '#...#', '#...#', '.####', '....#', '...#.', '.##..'],
  ' ': ['.....', '.....', '.....', '.....', '.....', '.....', '.....'],
  '!': ['..#..', '..#..', '..#..', '..#..', '..#..', '.....', '..#..'],
  '?': ['.###.', '#...#', '....#', '...#.', '..#..', '.....', '..#..'],
  '.': ['.....', '.....', '.....', '.....', '.....', '.....', '..#..'],
  '-': ['.....', '.....', '.....', '#####', '.....', '.....', '.....'],
  '+': ['.....', '..#..', '..#..', '#####', '..#..', '..#..', '.....'],
  // A small heart, handy for "MOM <3" style bracelets.
  '<3': ['.#.#.', '#####', '#####', '#####', '.###.', '.###.', '..#..'],
};

export const GLYPH_HEIGHT = 7;
export const GLYPH_WIDTH = 5;

export function supportedCharacters(): string[] {
  return Object.keys(GLYPHS);
}

export function isRenderable(text: string): boolean {
  return tokenize(text).every((t) => t in GLYPHS);
}

/** Split into glyph keys, treating "<3" as one character. */
export function tokenize(text: string): string[] {
  const out: string[] = [];
  const upper = text.toUpperCase();
  for (let i = 0; i < upper.length; i++) {
    if (upper.startsWith('<3', i)) {
      out.push('<3');
      i++;
      continue;
    }
    out.push(upper[i]!);
  }
  return out;
}

/** Characters we cannot knot, so the UI can say so plainly instead of guessing. */
export function unsupportedCharacters(text: string): string[] {
  return [...new Set(tokenize(text).filter((t) => !(t in GLYPHS)))];
}

export interface RenderedWord {
  /** grid[row][col]: true where the letter colour shows. */
  grid: boolean[][];
  cols: number;
  rows: number;
  /** Characters that were dropped because there is no glyph for them. */
  skipped: string[];
}

/**
 * Render text into a bracelet grid.
 *
 * `margin` is the number of plain background strings on each edge. Rows run
 * along the length of the bracelet, columns across its width.
 */
export function renderWord(text: string, margin = 2, letterGap = 1): RenderedWord {
  const tokens = tokenize(text);
  const skipped: string[] = [];
  const usable = tokens.filter((t) => {
    if (t in GLYPHS) return true;
    skipped.push(t);
    return false;
  });

  const cols = GLYPH_HEIGHT + margin * 2;
  if (usable.length === 0) {
    return { grid: [], cols, rows: 0, skipped };
  }

  // Rotate the glyph a quarter turn so the text runs along the bracelet's
  // length. The glyph's x axis becomes the row (knotting) axis and its y axis
  // becomes the column (width) axis, MIRRORED.
  //
  // The mirror is load-bearing, not a stylistic choice. A plain transpose
  // (row = gx, col = gy) is a reflection across the diagonal, so the finished
  // bracelet would spell the word backwards no matter which way you turned it.
  // Reversing the column axis turns the transpose into a true 90 degree
  // rotation: hold the chart and turn it counter-clockwise (so the left-hand
  // edge of the bracelet becomes the bottom of the text) and the word reads
  // correctly, first-knotted letter first.
  const rows: boolean[][] = [];
  // A blank row before the first letter keeps the text off the very first knot.
  pushBlank(rows, cols, letterGap);

  usable.forEach((token, i) => {
    const glyph = GLYPHS[token]!;
    for (let gx = 0; gx < GLYPH_WIDTH; gx++) {
      const row = new Array<boolean>(cols).fill(false);
      for (let gy = 0; gy < GLYPH_HEIGHT; gy++) {
        row[cols - 1 - (margin + gy)] = glyph[gy]![gx] === '#';
      }
      rows.push(row);
    }
    if (i < usable.length - 1) pushBlank(rows, cols, letterGap);
  });

  pushBlank(rows, cols, letterGap);

  return { grid: rows, cols, rows: rows.length, skipped };
}

function pushBlank(rows: boolean[][], cols: number, n: number): void {
  for (let i = 0; i < n; i++) rows.push(new Array<boolean>(cols).fill(false));
}

/** Debug/tests: draw a rendered word as ASCII so a human can read it. */
export function wordToAscii(r: RenderedWord): string {
  return r.grid.map((row) => row.map((c) => (c ? '#' : '.')).join('')).join('\n');
}

/**
 * The same grid turned a quarter turn counter-clockwise, which is the
 * orientation the word is actually readable in. Previews use this to show the
 * crafter the word the right way up; tests use it to prove the rotation in
 * `renderWord` is a rotation and not a mirror.
 */
export function rotateForReading(grid: boolean[][]): boolean[][] {
  const rowCount = grid.length;
  if (rowCount === 0) return [];
  const colCount = grid[0]!.length;
  const out: boolean[][] = [];
  for (let i = 0; i < colCount; i++) {
    const line = new Array<boolean>(rowCount).fill(false);
    for (let j = 0; j < rowCount; j++) line[j] = grid[j]![colCount - 1 - i]!;
    out.push(line);
  }
  return out;
}

/** `rotateForReading` rendered as ASCII. */
export function readableAscii(r: RenderedWord): string {
  return rotateForReading(r.grid)
    .map((row) => row.map((c) => (c ? '#' : '.')).join(''))
    .join('\n');
}
